import React from 'react';
import { motion } from 'framer-motion';
import { SlidersHorizontal, Wand2, Replace, CheckCheck, Sparkles, Check } from 'lucide-react';
/* Compact code mocks — one per step, in the deck's device-frame language. */
function StepMock({
  variant
}: {
  variant: number;
}) {
  return <div className="w-full aspect-[4/3] bg-[var(--bg)] rounded-2xl border border-[var(--surface-alt)] shadow-sm overflow-hidden p-3 flex flex-col gap-2 text-left" data-id="element-215">
      {variant === 0 && <>
          <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--fg-muted)]" data-id="element-216">
            Your goals
          </div>
          {[{
        l: 'Calories',
        w: 'w-2/3'
      }, {
        l: 'Budget',
        w: 'w-1/2'
      }, {
        l: 'Protein',
        w: 'w-4/5'
      }].map((row, i) => <div key={i} className="flex items-center gap-2" data-id="element-217">
              <span className="text-[9px] text-[var(--fg-muted)] w-12 shrink-0" data-id="element-218">
                {row.l}
              </span>
              <div className="flex-1 h-1.5 bg-[var(--surface-alt)] rounded-full relative" data-id="element-219">
                <div className={`h-1.5 bg-[var(--accent)] rounded-full ${row.w}`} data-id="element-220" />
              </div>
            </div>)}
        </>}
      {variant === 1 && <>
          <div className="flex items-center gap-1.5 text-[var(--accent)] text-[9px] font-bold uppercase tracking-widest" data-id="element-221">
            <Sparkles size={10} data-id="element-222" /> Drafting…
          </div>
          <div className="grid grid-cols-3 gap-1.5 flex-1" data-id="element-223">
            {Array.from({
          length: 6
        }).map((_, i) => <div key={i} className="rounded-md bg-[var(--surface-alt)]/70" data-id="element-224" />)}
          </div>
        </>}
      {variant === 2 && <>
          <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--fg-muted)]" data-id="element-225">
            Swap meal
          </div>
          <div className="h-7 rounded-lg bg-white border border-[var(--surface-alt)] flex items-center px-2 gap-2" data-id="element-226">
            <div className="w-4 h-4 rounded bg-[var(--surface-alt)]" data-id="element-227" />
            <div className="h-1.5 flex-1 bg-[var(--surface-alt)] rounded-full" data-id="element-228" />
            <Replace size={12} className="text-[var(--accent)]" data-id="element-229" />
          </div>
          <div className="h-7 rounded-lg bg-[var(--accent)]/10 border border-[var(--accent)]/30 flex items-center px-2 gap-2" data-id="element-230">
            <div className="w-4 h-4 rounded bg-[var(--accent)]/40" data-id="element-231" />
            <div className="h-1.5 flex-1 bg-[var(--accent)]/40 rounded-full" data-id="element-232" />
            <Check size={12} className="text-[var(--accent)]" data-id="element-233" />
          </div>
        </>}
      {variant === 3 && <>
          <div className="flex justify-between items-center" data-id="element-234">
            <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--fg-muted)]" data-id="element-235">
              Cart
            </div>
            <div className="text-[10px] font-bold text-[var(--fg)]" data-id="element-236">$84.50</div>
          </div>
          <div className="flex-1 space-y-1.5" data-id="element-237">
            <div className="h-1.5 w-full bg-[var(--surface-alt)] rounded-full" data-id="element-238" />
            <div className="h-1.5 w-3/4 bg-[var(--surface-alt)] rounded-full" data-id="element-239" />
          </div>
          <div className="h-7 rounded-lg bg-[var(--fg)] text-[var(--bg)] flex items-center justify-center gap-1.5 text-[9px] font-bold" data-id="element-240">
            <Check size={11} data-id="element-241" /> Checkout
          </div>
        </>}
    </div>;
}
export function Slide07HowItWorks() {
  const steps = [{
    icon: SlidersHorizontal,
    title: 'Set your goals',
    text: 'Budget, calories, diet, and household size — set once, adjusted anytime.'
  }, {
    icon: Wand2,
    title: 'AI drafts your week',
    text: 'Seven balanced days appear instantly, tuned to your targets.'
  }, {
    icon: Replace,
    title: 'Tweak & swap',
    text: 'Not feeling Tuesday? Swap any meal for a smart alternative.'
  }, {
    icon: CheckCheck,
    title: 'One tap to cart',
    text: 'Approve the plan and the full cart is built and priced for you.'
  }];
  return <div className="w-full h-full flex flex-col justify-center px-[clamp(4rem,8vw,10rem)]" data-id="element-242">
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-14 text-center" data-id="element-243">
        <span className="text-[var(--accent)] font-semibold tracking-widest uppercase text-sm mb-4 block" data-id="element-244">
          How It Works
        </span>
        <h2 className="font-display text-[clamp(2.5rem,4vw,4rem)] leading-tight" data-id="element-245">
          From goals to a full cart in four steps.
        </h2>
      </motion.div>

      <div className="relative max-w-6xl mx-auto w-full" data-id="element-246">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-[clamp(1.5rem,3vw,2.5rem)] relative" data-id="element-247">
          {steps.map((s, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + i * 0.12,
          type: 'spring',
          stiffness: 160,
          damping: 20
        }} className="flex flex-col items-center text-center" data-id="element-248">
              <div className="w-full mb-6 relative" data-id="element-249">
                <StepMock variant={i} data-id="element-250" />
                <span className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] text-sm font-semibold flex items-center justify-center shadow-md z-10" data-id="element-251">
                  {i + 1}
                </span>
              </div>
              <div className="w-11 h-11 rounded-2xl bg-[var(--surface)] border border-[var(--surface-alt)] shadow-sm flex items-center justify-center text-[var(--accent)] mb-4" data-id="element-252">
                <s.icon size={22} strokeWidth={1.75} data-id="element-253" />
              </div>
              <h3 className="font-display text-xl mb-2" data-id="element-254">{s.title}</h3>
              <p className="text-[var(--fg-muted)] leading-relaxed text-sm max-w-[16rem]" data-id="element-255">
                {s.text}
              </p>
            </motion.div>)}
        </div>
      </div>
    </div>;
}